import { createContext, lazy, Suspense, useContext, useState } from "react";
import { Hash } from "lucide-react";
import type { Block, ComponentBlock, HeadingBlock } from "@/lib/blocks";
import { cn, copyText } from "@/lib/utils";
import { Markdown } from "@/components/Markdown";
import { Callout } from "./Callout";
import { Checklist } from "./Checklist";
import { TableBlock } from "./TableBlock";
import { MermaidBlock } from "./Mermaid";
import { Decision } from "./Decision";
import { Timeline } from "./Timeline";
import { Cards } from "./Cards";
import { Stats } from "./Stats";
import { InvalidBlock } from "./InvalidBlock";

// Recharts is heavy; load it only when a note actually has a chart.
const Chart = lazy(() => import("./Chart").then((m) => ({ default: m.Chart })));

export interface BlockActions {
  /** Called when a checklist box is clicked. Absent → boxes are read-only. */
  onToggleCheck?: (blockId: string, index: number, checked: boolean) => void;
  /** Block ids with a toggle in flight (disables their boxes). */
  pending?: ReadonlySet<string>;
}

const BlockCtx = createContext<BlockActions>({});
export const useBlockActions = () => useContext(BlockCtx);

export function BlockRenderer({
  blocks,
  actions,
  nested = false,
}: {
  blocks: Block[];
  actions?: BlockActions;
  nested?: boolean;
}) {
  const inner = (
    <div className={cn("flex flex-col", nested ? "gap-3" : "gap-5")}>
      {blocks.map((b, i) => (
        <BlockView key={b.type === "markdown" ? `md-${i}` : b.id} block={b} nested={nested} />
      ))}
    </div>
  );
  return actions ? <BlockCtx.Provider value={actions}>{inner}</BlockCtx.Provider> : inner;
}

function BlockView({ block, nested }: { block: Block; nested: boolean }) {
  switch (block.type) {
    case "heading":
      return <Heading block={block} />;
    case "markdown":
      return <Markdown compact={nested}>{block.raw}</Markdown>;
    case "component":
      return <ComponentView block={block} nested={nested} />;
    default:
      return null;
  }
}

/* ---------------- headings ---------------- */

const HEADING_CLASS: Record<number, string> = {
  1: "text-[1.75rem] leading-tight mt-2",
  2: "text-[1.375rem] leading-snug mt-6 pb-1.5 border-b border-border",
  3: "text-lg leading-snug mt-4",
  4: "text-base mt-3",
  5: "text-sm uppercase tracking-wide text-muted-foreground mt-3",
  6: "text-sm text-muted-foreground mt-3",
};

function Heading({ block }: { block: HeadingBlock }) {
  const Tag = `h${block.depth}` as keyof JSX.IntrinsicElements;
  return (
    <Tag
      id={block.id}
      className={cn(
        "group/heading relative scroll-mt-24 font-semibold tracking-[-0.01em] text-foreground",
        HEADING_CLASS[block.depth],
      )}
    >
      <a
        href={`#${block.id}`}
        className="absolute -left-6 top-1/2 hidden -translate-y-1/2 text-muted-foreground/60 opacity-0 transition-opacity hover:text-foreground group-hover/heading:opacity-100 md:block"
        aria-label={`Link to ${block.text}`}
      >
        <Hash className="size-4" />
      </a>
      {block.text}
    </Tag>
  );
}

/* ---------------- components ---------------- */

function ComponentView({ block, nested }: { block: ComponentBlock; nested: boolean }) {
  return (
    <ComponentFrame id={block.id} name={block.name}>
      <ComponentBody block={block} nested={nested} />
    </ComponentFrame>
  );
}

function ComponentBody({ block, nested }: { block: ComponentBlock; nested: boolean }) {
  if (block.error) return <InvalidBlock block={block} />;
  const p = (block.props ?? {}) as Record<string, unknown>;
  switch (block.name) {
    case "callout":
      return <Callout props={p} body={block.body} />;
    case "checklist":
      return <Checklist id={block.id} props={p} body={block.body} />;
    case "table":
      return <TableBlock props={p} body={block.body} />;
    case "mermaid":
      return <MermaidBlock source={block.body} />;
    case "decision":
      return <Decision props={p} />;
    case "timeline":
      return <Timeline props={p} />;
    case "cards":
      return <Cards props={p} />;
    case "stats":
      return <Stats props={p} />;
    case "chart":
      return (
        <Suspense fallback={<div className="h-64 animate-pulse rounded-xl border bg-muted/40" aria-busy="true" />}>
          <Chart props={p} />
        </Suspense>
      );
    case "columns": {
      const count = p.count === 3 ? 3 : 2;
      const children = block.children ?? [];
      if (nested) return <InvalidBlock block={{ ...block, error: "columns cannot contain columns" }} />;
      return (
        <div className={cn("grid gap-4", count === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2")}>
          {splitColumns(children, count).map((col, i) => (
            <div key={i} className="min-w-0">
              <BlockRenderer blocks={col} nested />
            </div>
          ))}
        </div>
      );
    }
    default:
      return <InvalidBlock block={{ ...block, error: block.error ?? `unknown component "${block.name}"` }} />;
  }
}

/**
 * Column children: if the parser grouped them (array of arrays via props.columns) use that,
 * else deal the blocks out: each top-level block becomes one column, overflow wraps.
 */
function splitColumns(children: Block[], count: number): Block[][] {
  if (children.length === 0) return [];
  if (children.length <= count) return children.map((c) => [c]);
  const per = Math.ceil(children.length / count);
  const cols: Block[][] = [];
  for (let i = 0; i < count; i++) cols.push(children.slice(i * per, (i + 1) * per));
  return cols;
}

/** Anchor + hover affordance showing the block id (handy for telling a model what to patch). */
function ComponentFrame({ id, name, children }: { id: string; name: string; children: React.ReactNode }) {
  const [copied, setCopied] = useState(false);
  return (
    <div id={id} data-block={name} className="group/block relative scroll-mt-24">
      {children}
      <button
        type="button"
        onClick={async () => {
          if (await copyText(id)) {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }
        }}
        title={`Block id: ${id} (click to copy)`}
        className={cn(
          "absolute -top-2.5 right-2 z-10 inline-flex h-5 items-center gap-1 rounded-full border bg-popover px-1.5 font-mono text-[10px] leading-none text-muted-foreground shadow-sm transition-opacity",
          "opacity-0 focus-visible:opacity-100 group-hover/block:opacity-100 group-focus-within/block:opacity-100",
          copied && "opacity-100 text-tone-tip",
        )}
      >
        <Hash className="size-2.5" />
        {copied ? "copied" : id}
      </button>
    </div>
  );
}
